<template>
  <v-flex
    id="expensesForMedicine"
    class="question"
  >
    <p class="mb-0 text-uppercase">
      {{ $t('sheet.sheetOne.hint_material') }}
    </p>
    <h2 class="display-3 display-3_dark">
      {{ getExpensesForMedicineQuestions.label }}
    </h2>
    <ol class="questions-list">
      <li
        v-if="getExpensesForMedicineQuestions.hasOwnProperty('has_ill_members')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getExpensesForMedicineQuestions.has_ill_members.required }"
        >
          {{ getExpensesForMedicineQuestions.has_ill_members.label }}
        </p>
        <ConfirmButtons
          v-model="hasIllMembers"
          v-validate="{ required: getExpensesForMedicineQuestions.has_ill_members.required }"
          class="mb-4"
          string
          :disabled="readOnly"
          :error-messages="errors.collect('hasIllMembers')"
          data-vv-as=" "
          :data-vv-name="'hasIllMembers'"
        />
      </li>
      <SheetQuestion
        v-if="
          getExpensesForMedicineQuestions.hasOwnProperty('selected_ill_members') &&
            hasIllMembers === 'yes'
        "
        :label="getExpensesForMedicineQuestions.selected_ill_members.label"
        :required="getExpensesForMedicineQuestions.selected_ill_members.required"
        custom
        list-sign="lNone"
      >
        <v-checkbox
          v-for="(member, index) in illMembers"
          :key="index"
          v-model="selectedIllMembers"
          v-validate="{ required: getExpensesForMedicineQuestions.selected_ill_members.required }"
          :disabled="readOnly"
          :label="member?.name"
          type="checkbox"
          off-icon=""
          on-icon="check"
          multiple
          :checked="
            selectedIllMembers.some((e) => {
              return e.id === index
            })
          "
          :value="member?.id"
          :error="errors.has('selectedIllMembers')"
          :error-messages="
            index + 1 === members.length ? errors.collect('selectedIllMembers') : null
          "
          data-vv-as=" "
          :data-vv-name="'selectedIllMembers'"
        />
        <div v-if="selectedIllMembers.length > 0">
          <p class="fw-600 f-2 mt-3">
            {{ getExpensesForMedicineQuestions.ill_members.label }}
          </p>
          <IllMembersFormNewVariant
            v-if="getExpensesForMedicineQuestions?.ill_members"
            :read-only="readOnly"
            :members="illMembers"
            :selected="selectedIllMembers"
          />
        </div>
      </SheetQuestion>
      <li
        v-if="
          !isCurrentEdition && getExpensesForMedicineQuestions.hasOwnProperty('has_disabled_member')
        "
        class="mt-4"
      >
        <p
          class="header custom lB"
          :class="{ required: getExpensesForMedicineQuestions.has_disabled_member.required }"
        >
          {{ getExpensesForMedicineQuestions.has_disabled_member.label }}
        </p>
        <ConfirmButtons
          v-model="hasDisabledMember"
          v-validate="{ required: getExpensesForMedicineQuestions.has_disabled_member.required }"
          class="mb-4"
          string
          :disabled="readOnly"
          :error-messages="errors.collect('hasDisabledMembers')"
          data-vv-as=" "
          :data-vv-name="'hasDisabledMembers'"
        />
      </li>
      <li
        v-if="
          getExpensesForMedicineQuestions.hasOwnProperty('has_disabled_members')
        "
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getExpensesForMedicineQuestions.has_disabled_members.required }"
        >
          {{ getExpensesForMedicineQuestions.has_disabled_members.label }}
        </p>
        <ConfirmButtons
          v-model="hasDisabledMembers"
          v-validate="{ required: getExpensesForMedicineQuestions.has_disabled_members.required }"
          class="mb-4"
          string
          :disabled="readOnly"
          :error-messages="errors.collect('hasDisabledMembers')"
          data-vv-as=" "
          :data-vv-name="'hasDisabledMembers'"
        />
      </li>
      <SheetQuestion
        v-if="
          getExpensesForMedicineQuestions.hasOwnProperty('selected_disabled_members') &&
            hasDisabledMembers === 'yes'
        "
        :label="getExpensesForMedicineQuestions.selected_disabled_members.label"
        :required="getExpensesForMedicineQuestions.selected_disabled_members.required"
        custom
        list-sign="lNone"
      >
        <v-checkbox
          v-for="(member, index) in disabledMembers"
          :key="index"
          v-model="selectedDisabledMembers"
          v-validate="{ required: getExpensesForMedicineQuestions.selected_ill_members.required }"
          :disabled="readOnly"
          :label="member.name"
          type="checkbox"
          off-icon=""
          on-icon="check"
          multiple
          :checked="
            selectedDisabledMembers.some((e) => {
              return e.id === index
            })
          "
          :value="member.id"
          :error="errors.has('selectedDisabledMembers')"
          :error-messages="
            index + 1 === members.length ? errors.collect('selectedDisabledMembers') : null
          "
          data-vv-as=" "
          :data-vv-name="'selectedDisabledMembers'"
        />
        <div v-if="selectedDisabledMembers.length > 0">
          <p class="fw-600 f-2 mt-3">
            {{ getExpensesForMedicineQuestions.disabled_members.label }}
          </p>
          <DisabledMembersForm
            :read-only="readOnly"
            :members="disabledMembers"
            :selected="selectedDisabledMembers"
          />
        </div>
      </SheetQuestion>
      <ExpensesSummary
        class="mt-4"
        :label="getExpensesForMedicineQuestions.total_medicine_amount.label"
        :total-amount="totalMedicineAmount"
      />
      <li
        v-if="
          (hasIllMembers === 'yes' || hasDisabledMember === 'yes') &&
            getExpensesForMedicineQuestions.hasOwnProperty('members')
        "
        class="mt-4"
      >
        <div v-if="getExpensesForMedicineQuestions.hasOwnProperty('selected_members')">
          <p
            class="header custom lC"
            :class="{ required: getExpensesForMedicineQuestions.selected_members.required }"
          >
            {{ getExpensesForMedicineQuestions.selected_members.label }}
          </p>
          <v-checkbox
            v-for="(member, index) in members"
            :key="index"
            v-model="selectedMembers"
            v-validate="{ required: getExpensesForMedicineQuestions.selected_members.required }"
            :disabled="readOnly"
            :label="member.name"
            type="checkbox"
            off-icon=""
            on-icon="check"
            multiple
            :checked="
              selectedMembers.some((e) => {
                return e.id === index
              })
            "
            :value="member.id"
            :error="errors.has('illMembers')"
            :error-messages="index + 1 === members.length ? errors.collect('illMembers') : null"
            data-vv-as=" "
            :data-vv-name="'illMembers'"
          />
        </div>
        <div v-if="selectedMembers.length > 0">
          <p class="fw-600 required f-2 mt-3">
            {{ getExpensesForMedicineQuestions.members.label }}
          </p>
          <IllMembersForm
            :read-only="readOnly"
            :members="members"
            :selected="selectedMembers"
          />
        </div>
      </li>
      <li
        v-if="getExpensesForMedicineQuestions.hasOwnProperty('covered_costs')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getExpensesForMedicineQuestions.covered_costs.required }"
        >
          {{ getExpensesForMedicineQuestions.covered_costs.label }}
        </p>
        <ConfirmButtons
          v-model="coveredCosts"
          v-validate="{ required: getExpensesForMedicineQuestions.covered_costs.required }"
          class="mb-4"
          string
          :disabled="readOnly"
          :error-messages="errors.collect('coveredCosts')"
          data-vv-as=" "
          :data-vv-name="'coveredCosts'"
        />
      </li>
      <li
        v-if="
          coveredCosts === 'no' &&
            getExpensesForMedicineQuestions.hasOwnProperty('covered_costs_explain')
        "
        class="mt-4"
      >
        <p
          class="header custom lA"
          :class="{ required: getExpensesForMedicineQuestions.covered_costs_explain.required }"
        >
          {{ getExpensesForMedicineQuestions.covered_costs_explain.label }}
        </p>
        <v-textarea
          v-model="coveredCostsExplain"
          v-validate="{
            ...validationRules.get('long_regular_field'),
            ...requiredRegularField(getExpensesForMedicineQuestions.covered_costs_explain.required)
          }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('coveredCostsExplain')"
          data-vv-as=" "
          data-vv-name="coveredCostsExplain"
          :required="getExpensesForMedicineQuestions.covered_costs_explain.required"
          :disabled="readOnly"
          auto-grow
        />
      </li>
      <li
        v-if="getExpensesForMedicineQuestions.hasOwnProperty('difficult_access_to_medicine')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{
            required: getExpensesForMedicineQuestions.difficult_access_to_medicine.required
          }"
        >
          {{ getExpensesForMedicineQuestions.difficult_access_to_medicine.label }}
        </p>
        <ConfirmButtons
          v-model="difficultAccessToMedicine"
          v-validate="{
            required: getExpensesForMedicineQuestions.difficult_access_to_medicine.required
          }"
          class="mb-4"
          string
          :disabled="readOnly"
          :error-messages="errors.collect('diffAccessToMedicine')"
          data-vv-as=" "
          :data-vv-name="'diffAccessToMedicine'"
        />
      </li>
      <li
        v-if="
          difficultAccessToMedicine === 'yes' &&
            getExpensesForMedicineQuestions.hasOwnProperty('difficult_access_to_medicine_reason')
        "
        class="mt-4"
      >
        <p
          class="header"
          :class="{
            required: getExpensesForMedicineQuestions.difficult_access_to_medicine_reason.required
          }"
        >
          {{ getExpensesForMedicineQuestions.difficult_access_to_medicine_reason.label }}
        </p>
        <v-textarea
          v-model="difficultAccessToMedicineReason"
          v-validate="{
            ...requiredRegularField(
              getExpensesForMedicineQuestions.difficult_access_to_medicine_reason.required
            )
          }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('difficultAccessToMedicineReason')"
          data-vv-as=" "
          data-vv-name="difficultAccessToMedicineReason"
          :required="getExpensesForMedicineQuestions.difficult_access_to_medicine_reason.required"
          :disabled="readOnly"
          auto-grow
        />
      </li>
      <li
        v-if="
          getExpensesForMedicineQuestions.hasOwnProperty('difficult_access_to_medical_assistance')
        "
        class="mt-4"
      >
        <p
          class="header"
          :class="{
            required:
              getExpensesForMedicineQuestions.difficult_access_to_medical_assistance.required
          }"
        >
          {{ getExpensesForMedicineQuestions.difficult_access_to_medical_assistance.label }}
        </p>
        <ConfirmButtons
          v-model="difficultAccessToMedicalAssistance"
          v-validate="{
            required:
              getExpensesForMedicineQuestions.difficult_access_to_medical_assistance.required
          }"
          class="mb-4"
          string
          :disabled="readOnly"
          :error-messages="errors.collect('difficultAccessToMedicalAssistance')"
          data-vv-as=" "
          :data-vv-name="'difficultAccessToMedicalAssistance'"
        />
      </li>
    </ol>
    <v-layout
      v-if="endButton != null"
      justify-center
      my-3
    >
      <v-flex shrink>
        <div>
          <v-btn
            color="sz_violet"
            :to="{ name: endButton.to }"
          >
            {{ endButton.text }}
          </v-btn>
        </div>
      </v-flex>
    </v-layout>
  </v-flex>
</template>

<script>
import ConfirmButtons from '@/components/widgets/sheet/ConfirmButtons';
import IllMembersForm from '@/components/widgets/sheet/IllMembersForm';

import validationRules from '@/constants/validationRules';
import formatDecimalVariables from '@/mixins/formatDecimalVariables';
import sheetValidationRules from '@/mixins/sheet/sheetValidationRules';

import DisabledMembersForm from '@/components//widgets/sheet/DisabledMembersForm.vue';
import SheetQuestion from '@/components/sheet/SheetQuestion.vue';
import ExpensesSummary from '@/components/widgets/sheet/ExpensesSummary.vue';
import IllMembersFormNewVariant from '@/components/widgets/sheet/IllMembersFormNewVariant.vue';
import debounce from 'lodash.debounce';
import uniqBy from 'lodash.uniqby';
import { mapGetters } from 'vuex';
import { createHelpers } from 'vuex-map-fields';

const { mapFields } = createHelpers({
    getterType: 'sheet/getExpensesForMedicineField',
    mutationType: 'sheet/updateExpensesForMedicineField'
});

export default {
    name: 'ExpensesForMedicine',
    components: {
        ConfirmButtons,
        IllMembersForm,
        SheetQuestion,
        IllMembersFormNewVariant,
        DisabledMembersForm,
        ExpensesSummary
    },
    mixins: [sheetValidationRules, formatDecimalVariables],
    inject: ['$validator'],
    props: {
        readOnly: {
            default: false
        },
        endButton: {
            default: null
        },
        isCurrentEdition: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        ...mapFields([
            'hasIllMembers',
            'hasDisabledMember',
            'hasDisabledMembers',
            'members',
            'illMembers',
            'disabledMembers',
            'selectedMembers',
            'selectedIllMembers',
            'selectedDisabledMembers',
            'coveredCosts',
            'coveredCostsExplain',
            'difficultAccessToMedicine',
            'difficultAccessToMedicineReason',
            'difficultAccessToMedicalAssistance',
            'totalMedicineAmount'
        ]),
        ...mapGetters('sheet', ['getExpensesForMedicineQuestions', 'getDoctorsPackQuestions']),
        representatives () {
            return this.$store.getters['sheet/getRepresentatives'];
        },
        illAndDisabledCostOfTreatment () {
            // Calculate total medical expanses from all illMembers and disabledMembers
            return (
                (this.illMembers?.reduce(
                    (previousValue, currentValue) =>
                        previousValue + this.formatDecimalVariable(currentValue?.monthlyCostOfTreatment),
                    0
                ) ?? 0) +
        (this.disabledMembers?.reduce(
            (previousValue, currentValue) =>
                previousValue + this.formatDecimalVariable(currentValue?.monthlyCostOfTreatment),
            0
        ) ?? 0)
            );
        },
        IllOrDisabledMemberSelected () {
            return this.selectedIllMembers?.length ?? 0 + this.selectedDisabledMembers?.length ?? 0;
        }
    },
    watch: {
        difficultAccessToMedicine: {
            immediate: true,
            handler (event) {
                if (this.getDoctorsPackQuestions && event === 'yes') {
                    this.$store.commit('sheet/removeHiddenSelector', 'doctorsPack');
                } else if (event === 'no') {
                    this.$store.commit('sheet/addHiddenSelector', 'doctorsPack');
                    this.difficultAccessToMedicineReason = null;
                }
            }
        },
        difficultAccessToMedicalAssistance: {
            immediate: true,
            handler (event) {
                if (this.isCurrentEdition) return;
                if (this.getDoctorsPackQuestions && event === 'yes') {
                    this.$store.commit('sheet/removeHiddenSelector', 'doctorsPack');
                } else if (event === 'no') {
                    this.$store.commit('sheet/addHiddenSelector', 'doctorsPack');
                }
            }
        },
        representatives: {
            handler: debounce(
                function (val) {
                    this.createMembers(val, this.members, this.selectedMembers, this.emptyMembersData);
                    this.createMembers(
                        val,
                        this.illMembers,
                        this.selectedIllMembers,
                        this.emptyIllMembersData
                    );
                    this.createMembers(
                        val,
                        this.disabledMembers,
                        this.selectedDisabledMembers,
                        this.emptyDisabledMembersData
                    );
                },
                1000,
                { leading: true }
            ),
            deep: true
        },
        coveredCosts (val) {
            if (val === 'yes') {
                this.coveredCostsExplain = null;
            }
        },
        hasIllMembers (val) {
            if (val === 'no') {
                this.$nextTick(() => {
                    this.selectedIllMembers = [];
                });
            }
            this.setTotalMedicineAmount(this.illAndDisabledCostOfTreatment);
        },
        hasDisabledMembers (val) {
            if (val === 'no') {
                this.$nextTick(() => {
                    this.selectedDisabledMembers = [];
                });
            }
            this.setTotalMedicineAmount(this.illAndDisabledCostOfTreatment);
        },
        selectedMembers (valN, valO) {
            this.onSelectedMembers(valN, valO, this.members);
        },
        selectedIllMembers (valN, valO) {
            this.onSelectedMembers(valN, valO, this.illMembers);
        },
        selectedDisabledMembers (valN, valO) {
            this.onSelectedMembers(valN, valO, this.disabledMembers);
        },
        illAndDisabledCostOfTreatment: {
            handler (value) {
                this.setTotalMedicineAmount(value);
            },
            immediate: true
        }
    },
    data () {
        return {
            validationRules,
            emptyMembersData: {
                illness: null,
                monthlyCostOfTreatment: null,
                note: null,
                disabilityOrigin: null,
                degreeOfDisability: null,
                complete: false
            },
            emptyIllMembersData: {
                illness: null,
                monthlyCostOfTreatment: null,
                note: null,
                memberIndependence: null,
                complete: false
            },
            emptyDisabledMembersData: {
                reasonForDisability: null,
                memberIndependence: null,
                monthlyCostOfTreatment: null,
                note: null,
                complete: false
            }
        };
    },
    created () {
        this.createMembers(
            this.representatives,
            this.members,
            this.selectedMembers,
            this.emptyMembersData
        );
        this.createMembers(
            this.representatives,
            this.illMembers,
            this.selectedIllMembers,
            this.emptyIllMembersData
        );
        this.createMembers(
            this.representatives,
            this.disabledMembers,
            this.selectedDisabledMembers,
            this.emptyDisabledMembersData
        );
    },
    beforeDestroy () {
        this.members = [];
        this.disabledMembers = [];
        this.illMembers = [];
    },
    methods: {
    // TODO #Refactor - there is simmilar logic in other components, we could create helper function and use across the app
    /**
     * @function
     * @param {Array<Object>} representatives - An array of representative objects. Each object must have an 'id' and 'firstName'.
     * @param {Array<Object>} members - An array of member objects to be updated or created.
     * @param {Array<number>} selected - An array containing the ids of selected members.
     * @param {Object} empty - An empty member object template which is used for creating new member objects.
     */
        createMembers (representatives, members, selected, empty) {
            // early return pattern if representatives is undefined/empty array
            if (representatives?.length < 1) return;
            representatives.forEach((representative) => {
                const memberIndex = members.findIndex((el) => el.id === representative.id);
                if (memberIndex >= 0) {
                    // member found, update name
                    members[memberIndex] = {
                        ...members[memberIndex],
                        name: representative.firstName
                    };
                } else {
                    // member not found, push new element using empty template
                    members.push({
                        ...empty,
                        id: representative.id,
                        name: representative.firstName
                    });
                }
            });
            // Remove element from members and selected members if element is no longer in representatives
            const representativeIds = Array.from(representatives, (x) => x.id);
            this.illMembers = this.illMembers.filter((member) => representativeIds.includes(member.id));
            this.illMembers = uniqBy(this.illMembers, 'id');
            this.disabledMembers = this.disabledMembers.filter((member) =>
                representativeIds.includes(member.id)
            );
            this.disabledMembers = uniqBy(this.disabledMembers, 'id');
            this.selectedIllMembers = this.selectedIllMembers.filter((selected) =>
                representativeIds.includes(selected)
            );
            this.selectedDisabledMembers = this.selectedDisabledMembers.filter((selected) =>
                representativeIds.includes(selected)
            );
        },
        setTotalMedicineAmount (amount) {
            if (this.hasDisabledMembers === 'yes' || this.hasIllMembers === 'yes') {
                this.totalMedicineAmount = amount;
            } else this.totalMedicineAmount = null;
        },
        onSelectedMembers (valN, valO, members) {
            if (valN.length < valO.length) {
                members.forEach((el) => {
                    if (!valN.includes(el.id)) {
                        for (const prop in el) {
                            if (Object.prototype.hasOwnProperty.call(el, prop)) {
                                if (prop === 'name' || prop === 'id') {
                                    continue;
                                }
                                if (Array.isArray(el[prop])) {
                                    el[prop] = [];
                                } else {
                                    el[prop] = null;
                                }
                            }
                        }
                    }
                });
            }
        }
    }
};
</script>

<style scoped></style>
